/**
 * Metabolic Vulnerability Index (MVX Plus) — proof-of-concept scoring model.
 *
 * These markers are NOT in the software yet (no Bloodwork fields, no DB
 * categories). This module is the self-contained "source of truth" that DEFINES
 * AND HOLDS the ranges for each MVX Plus marker so a gauge + pattern panel can be
 * designed against it. Pure functions, unit-tested independently, same split as
 * `computeI3` / `resolveThyroidPattern`. Inputs are a plain value bag (`MvxInputs`)
 * rather than `Bloodwork` so nothing needs wiring before the POC can run.
 *
 * TWO LAYERS, mirroring the thyroid model:
 *  1. The six MEASURED factors (GlycA, small HDL-P, citrate, valine, leucine,
 *     isoleucine) each get a held range → `markerState` (low/opt/high) and
 *     `markerZone` (green/yellow/orange/red). The factor states drive patterns.
 *  2. The three CALCULATED indices (MVX, IVX, MMX) are proprietary Labcorp NMR
 *     outputs — they CANNOT be recomputed from the six factors, so we HOLD them
 *     as entered values and only map them to a zone via their decision limits.
 *     MVX is the native 0–100 hero gauge; IVX/MMX are the two ranked sub-bars.
 *
 * PROVENANCE of the held numbers:
 *  • Lab-exact (Labcorp "Sample Report, 123601"): units (citrate mg/dL ⚠, the
 *    other five µmol/L); factor reference intervals (GlycA <400, small HDL-P
 *    11.5–21.6, citrate 1.08–2.73, valine 116–298, leucine 41–179, isoleucine
 *    28–109); index scale 1–100; and index decision limits, which DIFFER BY
 *    SPECIMEN TYPE — the printed report flags against PLASMA limits (MVX <63,
 *    IVX <59, MMX <71); the serum limits are MVX <58, IVX <62, MMX <56 (serum
 *    runs MVX −5, MMX −15, IVX +3 vs EDTA plasma). We hold both; `indexZone`
 *    defaults to plasma to match the printed report.
 *  • Practitioner-optimal (Dr. Darren Schmidt / BetterHealthGuy Ep. 216) — the
 *    functional targets, tighter than the lab interval: GlycA <300, small HDL-P
 *    ≥19 (higher is better — the lab flags >21.6 "high" but that direction is
 *    protective, so we do NOT penalize it), citrate high-normal ≤2.0, BCAA
 *    mid-range (Val ~200 / Leu ~110 / Ile ~55).
 *  • Tunable design choices: the `severe` (red) outer anchors and the index
 *    green/red margins — sensible clinical picks; adjust as validation accrues.
 *
 * Wording note: practitioner-facing names may be clinical; `clientTitle` on every
 * pattern is intentionally observational / non-diagnostic (no disease names, and
 * "reduced insulin sensitivity" is NEVER surfaced as "insulin resistance").
 */

/** Labcorp MVX Plus (test #123601) test-page URL. */
export const LABCORP_MVX_PLUS_URL = 'https://www.labcorp.com/tests/123601/metabolic-vulnerability-index-mvx-plus-by-nmr';

/** Gauge zone for a value — matches the LabsmartsGauge / bar palette. */
export type MvxZone = 'green' | 'yellow' | 'orange' | 'red';

/** Per-marker state relative to its optimal band. 'absent' = not measured. */
export type MvxTri = 'low' | 'opt' | 'high' | 'absent';

/** Specimen type — shifts the index decision limits (see header). */
export type Specimen = 'plasma' | 'serum';

/**
 * How a marker's optimal band should be read:
 *  • 'lowerBetter'  — optimal is a ceiling; only being ABOVE it penalizes (GlycA).
 *  • 'higherBetter' — optimal is a floor; only being BELOW it penalizes (small HDL-P).
 *  • 'bandOptimal'  — optimal is a window; deviating EITHER way penalizes, and the
 *                     direction of the miss selects the pattern (citrate, BCAAs).
 */
export type MvxDirection = 'lowerBetter' | 'higherBetter' | 'bandOptimal';

export type MvxFactorId = 'glycA' | 'sHdlP' | 'citrate' | 'valine' | 'leucine' | 'isoleucine';
export type MvxIndexId = 'mvx' | 'ivx' | 'mmx';

/** A measured MVX factor and the ranges we hold for it. */
export interface MvxMarkerConfig {
  /** Stable key, also the future Bloodwork field id. */
  id: MvxFactorId;
  /** Sub-index this factor rolls up into. */
  index: 'IVX' | 'MMX';
  /** Display label. */
  label: string;
  /** Short label for the arrow / marker row. */
  short: string;
  /** Units of measure. NOTE: citrate is mg/dL; every other factor is µmol/L. */
  units: string;
  direction: MvxDirection;
  /** Green band [lo, hi] — the optimal window (practitioner target). */
  optimal: [number, number];
  /** Lab reference interval [lo, hi] (exact, per Sample Report 123601). */
  reference: [number, number];
  /** Outer red anchors [lo, hi] — beyond these = red (severe). Tunable. */
  severe: [number, number];
  /** One-line practitioner interpretation of an out-of-range result. */
  note: string;
}

/**
 * The six measured factors, with the ranges held per the provenance header.
 * `optimal`/`reference`/`severe` bounds are always written [low, high]; the
 * `direction` decides which side actually penalizes.
 */
export const MVX_MARKERS: MvxMarkerConfig[] = [
  {
    id: 'glycA',
    index: 'IVX',
    label: 'GlycA',
    short: 'GlycA',
    units: 'µmol/L',
    direction: 'lowerBetter',
    optimal: [0, 300], // Schmidt: ideal < 300; best observed 236
    reference: [0, 400], // lab-exact
    severe: [0, 700], // 700+ = "really sick"
    note: 'Elevated inflammatory / infection signal (tracks IL-6, CRP, fibrinogen); pursue infection + hypercoagulation work-up.',
  },
  {
    id: 'sHdlP',
    index: 'IVX',
    label: 'Small HDL Particle Number',
    short: 'Small HDL-P',
    units: 'µmol/L',
    direction: 'higherBetter', // higher is protective; lab flags >21.6 "high" but we do NOT penalize it
    optimal: [19, 22], // 19–22 ideal; lab-high is fine
    reference: [11.5, 21.6], // lab-exact
    severe: [7, 21.6], // 6.7 = worst observed
    note: 'Low = toxic burden / impaired detox (liver → mold/metals/chemicals); also consumed by active inflammation.',
  },
  {
    id: 'citrate',
    index: 'MMX',
    label: 'Citrate',
    short: 'Citrate',
    units: 'mg/dL', // ⚠ mg/dL, unlike the other five
    direction: 'bandOptimal',
    optimal: [1.6, 2.0], // want high-normal, just under 2.0
    reference: [1.08, 2.73], // lab-exact
    severe: [0.8, 3.5],
    note: 'High = mitochondrial dysfunction / toxicity / nutrient (esp. B1) deficiency; low = thin Krebs-cycle substrate.',
  },
  {
    id: 'valine',
    index: 'MMX',
    label: 'Valine',
    short: 'Valine',
    units: 'µmol/L',
    direction: 'bandOptimal',
    optimal: [180, 230], // mid-range target ~200 (ref mid 207)
    reference: [116, 298], // lab-exact
    severe: [90, 470], // red only beyond ~1.6× lab-high; sits above the abnormal-report 405
    note: 'High = reduced insulin sensitivity / impaired BCAA catabolism; low = protein-energy malnutrition.',
  },
  {
    id: 'leucine',
    index: 'MMX',
    label: 'Leucine',
    short: 'Leucine',
    units: 'µmol/L',
    direction: 'bandOptimal',
    optimal: [95, 130], // mid-range target ~110 (ref mid 110)
    reference: [41, 179], // lab-exact
    severe: [34, 265], // above the abnormal-report 224
    note: 'High = reduced insulin sensitivity / impaired BCAA catabolism; low = protein-energy malnutrition.',
  },
  {
    id: 'isoleucine',
    index: 'MMX',
    label: 'Isoleucine',
    short: 'Isoleucine',
    units: 'µmol/L',
    direction: 'bandOptimal',
    optimal: [45, 70], // Schmidt target ~55, bracketing the ref mid 68
    reference: [28, 109], // lab-exact
    severe: [22, 175], // above the abnormal-report 150
    note: 'High = reduced insulin sensitivity / impaired BCAA catabolism; low = protein-energy malnutrition.',
  },
];

/** A calculated index and the thresholds we hold for it. */
export interface MvxIndexConfig {
  id: MvxIndexId;
  label: string;
  short: string;
  /** Client-facing, non-diagnostic one-liner: what this score means to the patient. */
  client: string;
  /** Top of the score scale (all three are 1–100). */
  scaleMax: number;
  /** Practical floor of the score (MMX rarely drops below ~43) — for BAR scaling. */
  floor: number;
  /** Printed-report medical decision limit (EDTA plasma) — at/above = out of range. */
  decisionLimitPlasma: number;
  /** Serum medical decision limit (serum runs different vs plasma). */
  decisionLimitSerum: number;
}

/** Green tops out this far below the decision limit; red starts this far above. Tunable. */
export const INDEX_GREEN_MARGIN = 20;
export const INDEX_RED_MARGIN = 15;

/**
 * The three indices. MVX is the hero gauge; IVX/MMX are the sub-bars. `floor`
 * matters for BAR scaling (draw MMX on ~40→60, not 0→100) — it does not affect
 * zones. These are proprietary NMR outputs: HELD from the report, never derived.
 */
export const MVX_INDICES: MvxIndexConfig[] = [
  { id: 'mvx', label: 'Metabolic Vulnerability Index', short: 'MVX', client: 'the overall metabolic-resilience score — the combined picture from the two sub-scores', scaleMax: 100, floor: 0, decisionLimitPlasma: 63, decisionLimitSerum: 58 },
  { id: 'ivx', label: 'Inflammation Vulnerability Index', short: 'IVX', client: 'How much inflammation or hidden infection the body carries', scaleMax: 100, floor: 0, decisionLimitPlasma: 59, decisionLimitSerum: 62 },
  { id: 'mmx', label: 'Metabolic Malnutrition Index', short: 'MMX', client: 'Whether the cells are well-nourished and well-fueled', scaleMax: 100, floor: 43, decisionLimitPlasma: 71, decisionLimitSerum: 56 },
];

// ─────────────────────────── factor scoring ───────────────────────────

/** Read one input value: `{ value, hasValue }`, blank/NaN → not entered. */
function readNumber(inputs: MvxInputs | undefined, id: string): { value: number; hasValue: boolean } {
  const raw = inputs ? (inputs as any)[id] : undefined;
  if (raw === undefined || raw === null || (raw as any) === '') return { value: 0, hasValue: false };
  const n = Number(raw);
  if (isNaN(n)) return { value: 0, hasValue: false };
  return { value: n, hasValue: true };
}

/** Classify a value against its optimal band, respecting the marker's direction. */
export function markerState(value: number, cfg: MvxMarkerConfig): MvxTri {
  const [optLo, optHi] = cfg.optimal;
  const penalizeHigh = cfg.direction !== 'higherBetter';
  const penalizeLow = cfg.direction !== 'lowerBetter';
  if (penalizeHigh && value > optHi) return 'high';
  if (penalizeLow && value < optLo) return 'low';
  return 'opt';
}

/**
 * Map a value to a gauge zone using the three held bands. Being on the "good"
 * side of the optimal window is always green (a low GlycA or a high small HDL-P
 * is never penalized); a miss on the penalized side steps green→yellow (past
 * optimal)→orange (past the lab reference)→red (past the severe anchor).
 */
export function markerZone(value: number, cfg: MvxMarkerConfig): MvxZone {
  const [optLo, optHi] = cfg.optimal;
  const [refLo, refHi] = cfg.reference;
  const [sevLo, sevHi] = cfg.severe;
  const penalizeHigh = cfg.direction !== 'higherBetter';
  const penalizeLow = cfg.direction !== 'lowerBetter';

  if (penalizeHigh && value > optHi) {
    if (value > sevHi) return 'red';
    if (value > refHi) return 'orange';
    return 'yellow';
  }
  if (penalizeLow && value < optLo) {
    if (value < sevLo) return 'red';
    if (value < refLo) return 'orange';
    return 'yellow';
  }
  return 'green';
}

// ─────────────────────────── index scoring ───────────────────────────

/** The specimen-specific medical decision limit for an index. */
export function indexLimit(cfg: MvxIndexConfig, specimen: Specimen = 'plasma'): number {
  return specimen === 'serum' ? cfg.decisionLimitSerum : cfg.decisionLimitPlasma;
}

/** Map a held index value to a zone via its (specimen-specific) decision limit. */
export function indexZone(value: number, cfg: MvxIndexConfig, specimen: Specimen = 'plasma'): MvxZone {
  const limit = indexLimit(cfg, specimen);
  if (value < limit - INDEX_GREEN_MARGIN) return 'green';
  if (value < limit) return 'yellow';
  if (value < limit + INDEX_RED_MARGIN) return 'orange';
  return 'red';
}

// ─────────────────────── bar geometry (drives the panel bars) ───────────────────────

export interface MvxBarSegment {
  zone: MvxZone;
  from: number;
  to: number;
}
export interface MvxBarTick {
  value: number;
  /** 'opt' = an optimal-band edge, 'lab' = a lab-reference edge. */
  kind: 'opt' | 'lab';
}
export interface MvxBar {
  domLo: number;
  domHi: number;
  segments: MvxBarSegment[];
  ticks: MvxBarTick[];
}

/**
 * Zone-banded range bar for one factor, mirroring the app's MiniReferenceRangeChart:
 * green = optimal band, yellow shoulders = lab reference, orange = alarm, red =
 * severe. The domain auto-expands to keep an out-of-range `value` on-scale, and
 * `ticks` are the optimal edges ('opt') and lab-reference edges ('lab') to label.
 * Contiguous by construction: segment `from`s chain to the previous `to`, the
 * first starts at `domLo`, the last ends at `domHi`.
 */
export function markerBar(cfg: MvxMarkerConfig, value: number): MvxBar {
  const [optLo, optHi] = cfg.optimal;
  const [refLo, refHi] = cfg.reference;
  const [sevLo, sevHi] = cfg.severe;
  const seg = (zone: MvxZone, from: number, to: number): MvxBarSegment => ({ zone, from, to });

  if (cfg.direction === 'lowerBetter') {
    const domHi = Math.max(sevHi, value) * 1.03;
    return {
      domLo: 0,
      domHi,
      segments: [seg('green', 0, optHi), seg('yellow', optHi, refHi), seg('orange', refHi, sevHi), seg('red', sevHi, domHi)],
      ticks: [{ value: optHi, kind: 'opt' }, { value: refHi, kind: 'lab' }],
    };
  }
  if (cfg.direction === 'higherBetter') {
    const domLo = Math.min(sevLo, value) * 0.9;
    const domHi = Math.max(optHi, refHi, value) * 1.07;
    return {
      domLo,
      domHi,
      segments: [seg('red', domLo, sevLo), seg('orange', sevLo, refLo), seg('yellow', refLo, optLo), seg('green', optLo, domHi)],
      ticks: [{ value: refLo, kind: 'lab' }, { value: optLo, kind: 'opt' }],
    };
  }
  // bandOptimal — symmetric, optimal band in the middle.
  const lo = Math.min(sevLo, value);
  const hi = Math.max(sevHi, value);
  const pad = (hi - lo) * 0.05;
  const domLo = lo - pad;
  const domHi = hi + pad;
  return {
    domLo,
    domHi,
    segments: [
      seg('red', domLo, sevLo),
      seg('orange', sevLo, refLo),
      seg('yellow', refLo, optLo),
      seg('green', optLo, optHi),
      seg('yellow', optHi, refHi),
      seg('orange', refHi, sevHi),
      seg('red', sevHi, domHi),
    ],
    ticks: [
      { value: refLo, kind: 'lab' },
      { value: optLo, kind: 'opt' },
      { value: optHi, kind: 'opt' },
      { value: refHi, kind: 'lab' },
    ],
  };
}

/**
 * Zone bands for an index bar (green→red around the decision limit). `domMax` is
 * the top of the bar's scale. None of the scores are capped at 100 — the MVX
 * headline and the IVX/MMX sub-scores have all been observed above 100 — so the
 * hero gauge and the panel bars pass a `domMax` wide enough to keep an
 * out-of-range value on-scale, and the red band runs out to that top.
 */
export function indexSegments(cfg: MvxIndexConfig, specimen: Specimen = 'plasma', domMax = 100): MvxBarSegment[] {
  const limit = indexLimit(cfg, specimen);
  const green = Math.max(0, limit - INDEX_GREEN_MARGIN);
  const redStart = Math.min(domMax, limit + INDEX_RED_MARGIN);
  return [
    { zone: 'green', from: 0, to: green },
    { zone: 'yellow', from: green, to: limit },
    { zone: 'orange', from: limit, to: redStart },
    { zone: 'red', from: redStart, to: domMax },
  ];
}

// ─────────────────────────── patterns ───────────────────────────

/*
 * POC categoryId block (900000–900099) — still confirm before DB upload.
 *
 * MOVED OFF 90000-90099 on 2026-09-02. That block is the Stress & Adrenal
 * family and every one of these six ids collided with a live adrenal pattern:
 * 90000 is Na/K high, 90010 Na/K low, 90020 cortisol low, 90030 evening
 * cortisol low, 90040 DHEA-S low. Nothing broke only because MVX is not wired
 * to the database yet; uploading would have overwritten shipped adrenal rows.
 *
 * 900000 continues the 100k-step convention the other libraries follow —
 * 500000 supplements, 600100 liver references, 700000 About descriptions,
 * 800000 range bibliography — and sits clear of all of them, the bibliography
 * being the highest in use at 801820.
 */
export enum MvxPatternId {
  Optimal = 900000,
  Inflammation = 900010,
  ToxicBurden = 900020,
  Mitochondrial = 900030,
  Malnutrition = 900040,
  CarbLoad = 900050,
}

export type MvxFactorStates = Record<MvxFactorId, MvxTri>;

export interface MvxPattern {
  id: MvxPatternId;
  key: string;
  /** Practitioner-facing name (clinical language allowed). */
  practitionerName: string;
  /** Client-facing title — observational, non-diagnostic. */
  clientTitle: string;
  /** Arrow row for the marker strip (mirrors the DB "Logic" column). */
  markerRow: string;
}

interface MvxPatternRule extends MvxPattern {
  /** Priority: lower fires first / is the dominant box. */
  priority: number;
  match: (s: MvxFactorStates) => boolean;
}

const bcaaKeys: MvxFactorId[] = ['valine', 'leucine', 'isoleucine'];
const anyBcaa = (s: MvxFactorStates, state: MvxTri): boolean => bcaaKeys.some(k => s[k] === state);

/**
 * Pattern rules in priority order. Not mutually exclusive: a client can carry an
 * IVX story (1–2) and an MMX story (3–5) at once, so `resolveMvxPatterns`
 * returns every match. Toxic-burden is suppressed when GlycA is high, because a
 * low small HDL-P is then better explained by active inflammation consuming it.
 */
const MVX_PATTERN_RULES: MvxPatternRule[] = [
  {
    id: MvxPatternId.Inflammation,
    key: 'inflammation',
    priority: 1,
    practitionerName: 'Inflammatory / infection burden',
    clientTitle: 'Elevated inflammatory activity',
    markerRow: '▲ GlycA',
    match: s => s.glycA === 'high',
  },
  {
    id: MvxPatternId.Mitochondrial,
    key: 'mitochondrial',
    priority: 2,
    practitionerName: 'Mitochondrial dysfunction / metabolic acidosis tendency',
    clientTitle: 'Reduced cellular-energy efficiency',
    markerRow: '▲ Citrate',
    match: s => s.citrate === 'high',
  },
  {
    id: MvxPatternId.CarbLoad,
    key: 'carbLoad',
    priority: 3,
    // Practitioner-facing may say the clinical term; client title must not.
    practitionerName: 'Reduced insulin sensitivity / impaired BCAA catabolism',
    clientTitle: 'Reduced carbohydrate tolerance',
    markerRow: '▲ Valine ▲ Leucine ▲ Isoleucine',
    match: s => anyBcaa(s, 'high'),
  },
  {
    id: MvxPatternId.Malnutrition,
    key: 'malnutrition',
    priority: 4,
    practitionerName: 'Metabolic malnutrition (protein-energy undernutrition)',
    clientTitle: 'Reduced amino-acid status',
    markerRow: '▼ Valine ▼ Leucine ▼ Isoleucine',
    match: s => anyBcaa(s, 'low'),
  },
  {
    id: MvxPatternId.ToxicBurden,
    key: 'toxicBurden',
    priority: 5,
    practitionerName: 'Toxic burden / impaired detoxification',
    clientTitle: 'Reduced detoxification capacity',
    markerRow: '▼ Small HDL-P',
    match: s => s.sHdlP === 'low' && s.glycA !== 'high',
  },
];

/** The "all clear" pattern, returned only when nothing else fires. */
export const MVX_OPTIMAL_PATTERN: MvxPattern = {
  id: MvxPatternId.Optimal,
  key: 'optimal',
  practitionerName: 'Low metabolic vulnerability',
  clientTitle: 'Optimal metabolic resilience',
  markerRow: '',
};

/**
 * Resolve factor states to every matching pattern, highest priority first.
 * Returns the Optimal pattern when at least one factor was assessed and nothing
 * fired; returns `[]` when nothing was assessed at all.
 */
export function resolveMvxPatterns(s: MvxFactorStates): MvxPattern[] {
  const anyAssessed = MVX_MARKERS.some(m => s[m.id] !== 'absent');
  if (!anyAssessed) return [];

  const matched = MVX_PATTERN_RULES.filter(r => r.match(s))
    .sort((a, b) => a.priority - b.priority)
    .map(({ priority, match, ...pattern }) => pattern);

  return matched.length > 0 ? matched : [MVX_OPTIMAL_PATTERN];
}

// ─────────────────────────── entry point ───────────────────────────

/**
 * Raw inputs. The six factors are lab values; the three indices are HELD from
 * the report (proprietary — not derivable here). Everything is optional so the
 * panel can render partial results as the practitioner enters them.
 */
export interface MvxInputs {
  glycA?: number;
  sHdlP?: number;
  citrate?: number;
  valine?: number;
  leucine?: number;
  isoleucine?: number;
  mvx?: number;
  ivx?: number;
  mmx?: number;
}

export interface MvxFactorResult extends MvxMarkerConfig {
  value: number;
  hasValue: boolean;
  state: MvxTri;
  zone: MvxZone;
}

export interface MvxIndexResult extends MvxIndexConfig {
  value: number;
  hasValue: boolean;
  zone: MvxZone;
}

export interface MvxAnalysis {
  factors: MvxFactorResult[];
  indices: MvxIndexResult[];
  states: MvxFactorStates;
  patterns: MvxPattern[];
  /** Highest-priority pattern for the hero box, or undefined if none assessed. */
  dominantPattern?: MvxPattern;
  anyEntered: boolean;
}

/** One-call entry point a panel would use — mirrors `computeI3`. */
export function analyzeMvx(inputs: MvxInputs | undefined, specimen: Specimen = 'plasma'): MvxAnalysis {
  let anyEntered = false;

  const factors: MvxFactorResult[] = MVX_MARKERS.map(cfg => {
    const { value, hasValue } = readNumber(inputs, cfg.id);
    if (hasValue) anyEntered = true;
    return {
      ...cfg,
      value,
      hasValue,
      state: hasValue ? markerState(value, cfg) : 'absent',
      zone: hasValue ? markerZone(value, cfg) : 'green',
    };
  });

  const indices: MvxIndexResult[] = MVX_INDICES.map(cfg => {
    const { value, hasValue } = readNumber(inputs, cfg.id);
    if (hasValue) anyEntered = true;
    return { ...cfg, value, hasValue, zone: hasValue ? indexZone(value, cfg, specimen) : 'green' };
  });

  const states = factors.reduce(
    (acc, f) => ({ ...acc, [f.id]: f.state }),
    {} as MvxFactorStates,
  );
  const patterns = resolveMvxPatterns(states);

  return { factors, indices, states, patterns, dominantPattern: patterns[0], anyEntered };
}
